import { useCallback, useMemo, useRef, useState } from "react";
import styles from "../../styles/components/PersonImportModal.module.css";
import { ReactComponent as CrossIcon } from "../../assets/icons/cross.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/add.svg";
import { contactsService } from "../../services/contacts.service";
import { useAuth } from "../../contexts/AuthContext";
import {
  getSelectableAccounts,
  resolveSelectableAccountId,
} from "../../utils/accountOptions";
import { describeRequestError } from "../../utils/requestErrorMessage";
import Toggle from "../../ui/selectors/Toggle";
import { useModalFocusTrap } from "../mailContent/useModalFocusTrap";
import { useOverlayClose } from "../../hooks/useOverlayClose";

const STEP_UPLOAD = "upload";
const STEP_PREVIEW = "preview";
const STEP_QUEUED = "queued";

const FIELD_LABELS = {
  position: "cargo",
  company: "empresa",
  country: "país",
  mobile: "celular",
  linkedin: "LinkedIn",
};

function describeRowAction(row) {
  if (row.status === "error") {
    return { label: "Error", className: styles.actionError };
  }
  if (row.action === "create") {
    return { label: "Crear", className: styles.actionCreate };
  }
  if (row.action === "update") {
    const filledFields = row.fields_to_fill
      .map((fieldName) => FIELD_LABELS[fieldName] ?? fieldName)
      .join(", ");
    if (row.reactivate) {
      const label = filledFields ? `Reactivar y completar: ${filledFields}` : "Reactivar";
      return { label, className: styles.actionUpdate };
    }
    if (!filledFields && row.company_action === "reactivate") {
      return { label: "Reactivar su empresa", className: styles.actionUpdate };
    }
    return { label: `Completar: ${filledFields}`, className: styles.actionUpdate };
  }
  return { label: "Sin cambios", className: "" };
}

function describeCompany(row) {
  const companyName = row.values.company;
  if (!companyName) return "-";
  if (row.company_action === "create") return `${companyName} (nueva)`;
  if (row.company_action === "reactivate") return `${companyName} (se reactiva)`;
  return companyName;
}

function pluralize(count, singular, plural) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function describeValidationCredits(validation, importableCount) {
  if (importableCount === 0) {
    return "No hay emails para validar.";
  }
  if (validation?.has_permission === false) {
    return "No tenés permiso para validar emails en esta cuenta.";
  }
  const requiredCredits = pluralize(validation?.credits_required ?? importableCount, "crédito", "créditos");
  if (validation?.is_unlimited) {
    return `Se van a consumir hasta ${requiredCredits} de validación (tenés créditos ilimitados).`;
  }
  const availableCredits = validation?.credits_available ?? 0;
  if (!validation?.has_enough_credits) {
    return `No tenés créditos de validación suficientes: necesitás ${requiredCredits} y tenés ${availableCredits}. Podés recargar desde Configuración → Créditos.`;
  }
  return `Se van a consumir hasta ${requiredCredits} de validación (tenés ${availableCredits} disponibles).`;
}

export default function PersonImportModal({ onClose }) {
  const { activeAccountId, accounts } = useAuth();
  const accountOptions = useMemo(() => getSelectableAccounts(accounts), [accounts]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const importAccountId =
    selectedAccountId ??
    resolveSelectableAccountId(accounts, activeAccountId) ??
    activeAccountId ??
    accountOptions[0]?.id ??
    null;

  const [step, setStep] = useState(STEP_UPLOAD);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showOnlyIssues, setShowOnlyIssues] = useState(false);
  const [validateEmails, setValidateEmails] = useState(false);
  const [queuedImport, setQueuedImport] = useState(null);
  const fileInputRef = useRef(null);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const handleClose = useCallback(() => {
    if (!loadingRef.current) onCloseRef.current();
  }, []);
  useModalFocusTrap(dialogRef, closeButtonRef, handleClose);

  const visibleRows = useMemo(() => {
    const rows = preview?.rows ?? [];
    if (!showOnlyIssues) return rows;
    return rows.filter((row) => row.errors.length > 0 || row.warnings.length > 0);
  }, [preview, showOnlyIssues]);

  const overlayCloseProps = useOverlayClose(loading ? undefined : onClose);

  function handleFileSelected(selectedFile) {
    if (!selectedFile) return;
    setFile(selectedFile);
    setErrorMessage(null);
  }

  async function handleDownloadTemplate() {
    try {
      await contactsService.downloadImportTemplate();
    } catch {
      setErrorMessage("No se pudo descargar el template. Intentá de nuevo.");
    }
  }

  async function handlePreview() {
    if (!file) return;
    setLoading(true);
    setErrorMessage(null);
    try {
      const previewData = await contactsService.importPreview({
        accountId: importAccountId,
        file,
      });
      setPreview(previewData);
      setShowOnlyIssues(false);
      setValidateEmails(
        Boolean(previewData.validation?.has_enough_credits) &&
          previewData.validation?.has_permission !== false,
      );
      setStep(STEP_PREVIEW);
    } catch (err) {
      const statusCode = err?.response?.status;
      if (statusCode === 400) {
        setErrorMessage(
          err.response?.data?.detail ?? "El archivo no se puede importar.",
        );
      } else if (statusCode === 403) {
        setErrorMessage(describeRequestError(err, "No se pudo revisar el archivo"));
      } else {
        setErrorMessage("Ocurrió un error inesperado. Intentá de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const queued = await contactsService.importConfirm(preview.import_id, {
        validateEmails: validateEmails && canValidate,
      });
      setQueuedImport(queued);
      setStep(STEP_QUEUED);
    } catch (err) {
      const statusCode = err?.response?.status;
      if (statusCode === 409 || statusCode === 404) {
        setErrorMessage(
          err.response?.data?.detail ?? "La precarga ya no está disponible.",
        );
      } else if (statusCode === 403) {
        setErrorMessage(describeRequestError(err, "No se pudo importar"));
      } else {
        setErrorMessage("Ocurrió un error inesperado al importar. Intentá de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  }

  function handleRestart() {
    setStep(STEP_UPLOAD);
    setShowOnlyIssues(false);
    setFile(null);
    setPreview(null);
    setQueuedImport(null);
    setErrorMessage(null);
  }

  const summary = preview?.summary;
  const importableCount = summary?.valid ?? 0;
  const canValidate =
    Boolean(preview?.validation?.has_enough_credits) &&
    preview?.validation?.has_permission !== false &&
    importableCount > 0;

  return (
    <div className={styles.overlay} {...overlayCloseProps}>
      <div
        ref={dialogRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="person-import-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <div>
            <div id="person-import-title" className={styles.title}>
              Importar contactos
            </div>
            <div className={styles.subtitle}>
              {step === STEP_UPLOAD && "Subí un archivo .csv o .xlsx de hasta 500 contactos (sin contar el encabezado)."}
              {step === STEP_PREVIEW && `Revisá el archivo ${preview?.file_name ?? ""} antes de importarlo.`}
              {step === STEP_QUEUED && "Import en proceso."}
            </div>
          </div>
          <button
            ref={closeButtonRef}
            className={styles.closeBtn}
            onClick={onClose}
            disabled={loading}
            aria-label="Cerrar"
            type="button"
          >
            <CrossIcon className={styles.closeIcon} />
          </button>
        </div>

        <div className={styles.body}>
          {step === STEP_UPLOAD && (
            <>
              {accountOptions.length > 1 && (
                <label className={styles.accountRow}>
                  Cuenta
                  <select
                    className={styles.accountSelect}
                    value={importAccountId ?? ""}
                    disabled={loading}
                    onChange={(event) => setSelectedAccountId(Number(event.target.value))}
                    aria-label="Cuenta donde se importan los contactos"
                  >
                    {accountOptions.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {!file ? (
                <div
                  className={`${styles.dropzone} ${dragging ? styles.dropzoneActive : ""}`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setDragging(false);
                    handleFileSelected(event.dataTransfer.files[0]);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <AddIcon style={{ width: 24, height: 24 }} />
                  <span>Arrastrá el archivo o hacé click para elegirlo</span>
                  <span className={styles.dropzoneHint}>.csv o .xlsx, hasta 500 contactos</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx"
                    className={styles.hiddenInput}
                    aria-label="Archivo a importar"
                    onChange={(event) => handleFileSelected(event.target.files[0])}
                    onClick={(event) => event.stopPropagation()}
                  />
                </div>
              ) : (
                <div className={styles.fileRow}>
                  <span className={styles.fileName}>{file.name}</span>
                  <button
                    className={styles.clearFileBtn}
                    onClick={() => setFile(null)}
                    disabled={loading}
                    aria-label="Quitar archivo"
                    type="button"
                  >
                    <CrossIcon className={styles.closeIcon} />
                  </button>
                </div>
              )}

              <button className={styles.templateLink} onClick={handleDownloadTemplate} type="button">
                Descargar template de .CSV
              </button>
              <ul className={styles.helpList}>
                <li>Nombre, apellido y email son obligatorios.</li>
                <li>
                  Si el email ya existe en tus contactos, solo se completan los datos
                  que estén vacíos.
                </li>
                <li>
                  La empresa se busca entre tus empresas por razón social y después
                  por nombre; si no existe, se crea.
                </li>
                <li>
                  Al revisar el archivo podés elegir validar los emails con
                  BounceBan; si no, se importan sin validar.
                </li>
              </ul>

              {loading && (
                <div className={styles.loadingState}>
                  <div className={styles.spinner} />
                  Revisando el archivo…
                </div>
              )}
            </>
          )}

          {step === STEP_PREVIEW && summary && (
            <>
              <div className={styles.summary}>
                <span className={styles.summaryChip}>
                  <strong>{summary.to_create}</strong> a crear
                </span>
                <span className={styles.summaryChip}>
                  <strong>{summary.to_update}</strong> a completar
                </span>
                <span className={styles.summaryChip}>
                  <strong>{summary.unchanged}</strong> sin cambios
                </span>
                <span className={styles.summaryChip}>
                  <strong>{summary.companies_to_create}</strong> empresas nuevas
                </span>
                {summary.with_errors > 0 && (
                  <span className={`${styles.summaryChip} ${styles.summaryChipError}`}>
                    <strong>{summary.with_errors}</strong> con errores (no se importan)
                  </span>
                )}
              </div>

              <label className={styles.filterRow}>
                <input
                  type="checkbox"
                  checked={showOnlyIssues}
                  onChange={(event) => setShowOnlyIssues(event.target.checked)}
                />
                Mostrar solo filas con errores o advertencias
              </label>

              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Fila</th>
                      <th>Contacto</th>
                      <th>Empresa</th>
                      <th>Acción</th>
                      <th>Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => {
                      const rowAction = describeRowAction(row);
                      return (
                        <tr key={row.line} data-testid={`import-row-${row.line}`}>
                          <td className={styles.cellMuted}>{row.line}</td>
                          <td>
                            <div>
                              {[row.values.first_name, row.values.last_name].filter(Boolean).join(" ") || "-"}
                            </div>
                            <div className={styles.cellMuted}>{row.values.email || "-"}</div>
                          </td>
                          <td>{describeCompany(row)}</td>
                          <td>
                            <span className={`${styles.actionBadge} ${rowAction.className}`}>
                              {rowAction.label}
                            </span>
                          </td>
                          <td>
                            <ul className={styles.issueList}>
                              {row.errors.map((error) => (
                                <li key={error} className={styles.issueError}>{error}</li>
                              ))}
                              {row.warnings.map((warning) => (
                                <li key={warning} className={styles.issueWarning}>{warning}</li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      );
                    })}
                    {visibleRows.length === 0 && (
                      <tr>
                        <td colSpan={5} className={styles.cellMuted}>
                          No hay filas con errores ni advertencias.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div
                className={`${styles.validateOption} ${canValidate ? "" : styles.validateOptionDisabled}`}
              >
                <Toggle
                  checked={validateEmails}
                  disabled={!canValidate}
                  onChange={setValidateEmails}
                  ariaLabel="Validar los emails importados con BounceBan"
                />
                <span>
                  Validar los emails importados con BounceBan
                  <div
                    className={canValidate || importableCount === 0 ? styles.validateHint : styles.validateHintError}
                  >
                    {describeValidationCredits(preview.validation, importableCount)}
                  </div>
                </span>
              </div>
            </>
          )}

          {step === STEP_QUEUED && queuedImport && (
            <div className={styles.notice} role="status">
              {queuedImport.validate_emails
                ? `Estamos validando los emails con BounceBan e importando ${pluralize(importableCount, "contacto", "contactos")}. Solo se van a guardar los que BounceBan marque como entregables.`
                : `Estamos importando ${pluralize(importableCount, "contacto", "contactos")}.`}{" "}
              Te vamos a avisar con una notificación cuando termine; desde ahí vas a
              poder ver las filas que no se importaron.
            </div>
          )}

          {errorMessage && (
            <div className={styles.errorText} role="alert">
              {errorMessage}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          {step === STEP_UPLOAD && (
            <>
              <button className={styles.secondaryBtn} onClick={onClose} type="button">
                Cancelar
              </button>
              <button
                className={styles.primaryBtn}
                onClick={handlePreview}
                disabled={!file || loading}
                type="button"
              >
                Revisar archivo
              </button>
            </>
          )}
          {step === STEP_PREVIEW && (
            <>
              <span className={styles.footerNote}>
                {`${pluralize(summary?.total ?? 0, "fila", "filas")} en el archivo`}
              </span>
              <button
                className={styles.secondaryBtn}
                onClick={handleRestart}
                disabled={loading}
                type="button"
              >
                Elegir otro archivo
              </button>
              <button
                className={styles.primaryBtn}
                onClick={handleConfirm}
                disabled={importableCount === 0 || loading}
                type="button"
              >
                {loading
                  ? "Importando…"
                  : `Importar ${pluralize(importableCount, "contacto", "contactos")}`}
              </button>
            </>
          )}
          {step === STEP_QUEUED && (
            <>
              <button
                className={styles.secondaryBtn}
                onClick={handleRestart}
                disabled={loading}
                type="button"
              >
                Importar otro archivo
              </button>
              <button
                className={styles.primaryBtn}
                onClick={onClose}
                disabled={loading}
                type="button"
              >
                Cerrar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
